
import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAngleLeft,
  faEnvelope,
  faUnlockAlt,
  faUser,
  faPhone,
  faMapMarkerAlt,
  faCode
} from "@fortawesome/free-solid-svg-icons";
import { Col, Row, Form, Card, Button, Container, InputGroup, Alert, Image } from '@themesberg/react-bootstrap';
import { Link, useHistory } from 'react-router-dom';
import ReCAPTCHA from "react-google-recaptcha";

import { Routes } from "../../routes";
import BgImage from "../../assets/img/illustrations/signin.svg";
import ProductivosLogo from "../../assets/img/productivos-logo.png";
import { registerUser } from "../../api/api";

export default () => {
  const history = useHistory();

  // Form state
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    code_expert: '',
    state: true,
    roleIds: []
  });

  // UI state
  const [captchaValue, setCaptchaValue] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Validate form
  const validateForm = () => {
    const errors = {};

    if (!formData.username.trim()) {
      errors.username = 'El nombre de usuario es requerido';
    }

    if (!formData.password) {
      errors.password = 'La contraseña es requerida';
    } else if (formData.password.length < 6) {
      errors.password = 'La contraseña debe tener al menos 6 caracteres';
    }

    if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Las contraseñas no coinciden';
    }

    if (!formData.firstName.trim()) {
      errors.firstName = 'El nombre es requerido';
    }

    if (!formData.lastName.trim()) {
      errors.lastName = 'El apellido es requerido';
    }

    if (!formData.email.trim()) {
      errors.email = 'El correo electrónico es requerido';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'El correo electrónico no es válido';
    }

    if (!captchaValue) {
      errors.captcha = 'Por favor, complete el CAPTCHA';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Prepare data for API (exclude confirmPassword)
      const { confirmPassword, ...userData } = formData;

      await registerUser(userData);

      // Redirect to login with success message
      const successMessage = encodeURIComponent('Se ha enviado un email de confirmación para la activación de su usuario.');
      history.push(`${Routes.Signin.path}?success=${successMessage}`);

    } catch (err) {
      console.error('Registration error:', err);

      // Extract error message from API response
      let errorMessage = 'Intente más tarde o comuníquese con el área de soporte.';

      if (err.response?.data?.message) {
        // Use the specific error message from the API
        errorMessage = err.response.data.message;
      }

      // Redirect to login with error message
      history.push(`${Routes.Signin.path}?error=${encodeURIComponent(errorMessage)}`);

    } finally {
      setLoading(false);
    }
  };

  // Handle CAPTCHA change
  const onCaptchaChange = (value) => {
    setCaptchaValue(value);
    if (validationErrors.captcha) {
      setValidationErrors(prev => ({
        ...prev,
        captcha: ''
      }));
    }
  };

  return (
    <main>
      <section className="d-flex align-items-center my-5 mt-lg-6 mb-lg-5">
        <Container>
          <p className="text-center">
            <Card.Link as={Link} to={Routes.Signin.path} className="text-gray-700">
              <FontAwesomeIcon icon={faAngleLeft} className="me-2" /> Volver al inicio de sesión
            </Card.Link>
          </p>
          <Row className="justify-content-center form-bg-image" style={{ backgroundImage: `url(${BgImage})` }}>
            <Col xs={12} className="d-flex align-items-center justify-content-center">
              <div className="mb-4 mb-lg-0 bg-white shadow-soft border rounded border-light p-4 p-lg-5 w-100 fmxw-500">
                <div className="text-center text-md-center mb-4 mt-md-0">
                  <Image src={ProductivosLogo} alt="Productivos.org" style={{ height: '60px', marginBottom: '20px' }} />
                  <h3 className="mb-0">Crear una cuenta</h3>
                </div>

                {success && (
                  <Alert variant="success">
                    ¡Registro exitoso! Redirigiendo al inicio de sesión...
                  </Alert>
                )}

                {error && (
                  <Alert variant="danger" onClose={() => setError('')} dismissible>
                    {error}
                  </Alert>
                )}

                <Form className="mt-4" onSubmit={handleSubmit}>
                  {/* Username */}
                  <Form.Group id="username" className="mb-3">
                    <Form.Label>Nombre de Usuario *</Form.Label>
                    <InputGroup>
                      <InputGroup.Text>
                        <FontAwesomeIcon icon={faUser} />
                      </InputGroup.Text>
                      <Form.Control
                        autoFocus
                        required
                        type="text"
                        name="username"
                        placeholder="usuario123"
                        value={formData.username}
                        onChange={handleChange}
                        isInvalid={!!validationErrors.username}
                      />
                      <Form.Control.Feedback type="invalid">
                        {validationErrors.username}
                      </Form.Control.Feedback>
                    </InputGroup>
                  </Form.Group>

                  {/* First Name */}
                  <Form.Group id="firstName" className="mb-3">
                    <Form.Label>Nombre *</Form.Label>
                    <InputGroup>
                      <InputGroup.Text>
                        <FontAwesomeIcon icon={faUser} />
                      </InputGroup.Text>
                      <Form.Control
                        required
                        type="text"
                        name="firstName"
                        placeholder="Juan"
                        value={formData.firstName}
                        onChange={handleChange}
                        isInvalid={!!validationErrors.firstName}
                      />
                      <Form.Control.Feedback type="invalid">
                        {validationErrors.firstName}
                      </Form.Control.Feedback>
                    </InputGroup>
                  </Form.Group>

                  {/* Last Name */}
                  <Form.Group id="lastName" className="mb-3">
                    <Form.Label>Apellido *</Form.Label>
                    <InputGroup>
                      <InputGroup.Text>
                        <FontAwesomeIcon icon={faUser} />
                      </InputGroup.Text>
                      <Form.Control
                        required
                        type="text"
                        name="lastName"
                        placeholder="Pérez"
                        value={formData.lastName}
                        onChange={handleChange}
                        isInvalid={!!validationErrors.lastName}
                      />
                      <Form.Control.Feedback type="invalid">
                        {validationErrors.lastName}
                      </Form.Control.Feedback>
                    </InputGroup>
                  </Form.Group>

                  {/* Email */}
                  <Form.Group id="email" className="mb-3">
                    <Form.Label>Correo Electrónico *</Form.Label>
                    <InputGroup>
                      <InputGroup.Text>
                        <FontAwesomeIcon icon={faEnvelope} />
                      </InputGroup.Text>
                      <Form.Control
                        required
                        type="email"
                        name="email"
                        placeholder="ejemplo@empresa.com"
                        value={formData.email}
                        onChange={handleChange}
                        isInvalid={!!validationErrors.email}
                      />
                      <Form.Control.Feedback type="invalid">
                        {validationErrors.email}
                      </Form.Control.Feedback>
                    </InputGroup>
                  </Form.Group>

                  {/* Phone */}
                  <Form.Group id="phone" className="mb-3">
                    <Form.Label>Teléfono</Form.Label>
                    <InputGroup>
                      <InputGroup.Text>
                        <FontAwesomeIcon icon={faPhone} />
                      </InputGroup.Text>
                      <Form.Control
                        type="tel"
                        name="phone"
                        placeholder="+57 300 123 4567"
                        value={formData.phone}
                        onChange={handleChange}
                      />
                    </InputGroup>
                  </Form.Group>

                  {/* Address */}
                  <Form.Group id="address" className="mb-3">
                    <Form.Label>Dirección</Form.Label>
                    <InputGroup>
                      <InputGroup.Text>
                        <FontAwesomeIcon icon={faMapMarkerAlt} />
                      </InputGroup.Text>
                      <Form.Control
                        type="text"
                        name="address"
                        placeholder="Calle 123 #45-67"
                        value={formData.address}
                        onChange={handleChange}
                      />
                    </InputGroup>
                  </Form.Group>

                  {/* Code Expert */}
                  <Form.Group id="code_expert" className="mb-3">
                    <Form.Label>Código de Experto</Form.Label>
                    <InputGroup>
                      <InputGroup.Text>
                        <FontAwesomeIcon icon={faCode} />
                      </InputGroup.Text>
                      <Form.Control
                        type="text"
                        name="code_expert"
                        placeholder="EXP-12345"
                        value={formData.code_expert}
                        onChange={handleChange}
                      />
                    </InputGroup>
                  </Form.Group>

                  {/* Password */}
                  <Form.Group id="password" className="mb-3">
                    <Form.Label>Contraseña *</Form.Label>
                    <InputGroup>
                      <InputGroup.Text>
                        <FontAwesomeIcon icon={faUnlockAlt} />
                      </InputGroup.Text>
                      <Form.Control
                        required
                        type="password"
                        name="password"
                        placeholder="Contraseña"
                        value={formData.password}
                        onChange={handleChange}
                        isInvalid={!!validationErrors.password}
                      />
                      <Form.Control.Feedback type="invalid">
                        {validationErrors.password}
                      </Form.Control.Feedback>
                    </InputGroup>
                  </Form.Group>

                  {/* Confirm Password */}
                  <Form.Group id="confirmPassword" className="mb-3">
                    <Form.Label>Confirmar Contraseña *</Form.Label>
                    <InputGroup>
                      <InputGroup.Text>
                        <FontAwesomeIcon icon={faUnlockAlt} />
                      </InputGroup.Text>
                      <Form.Control
                        required
                        type="password"
                        name="confirmPassword"
                        placeholder="Confirmar Contraseña"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        isInvalid={!!validationErrors.confirmPassword}
                      />
                      <Form.Control.Feedback type="invalid">
                        {validationErrors.confirmPassword}
                      </Form.Control.Feedback>
                    </InputGroup>
                  </Form.Group>

                  {/* reCAPTCHA */}
                  <Form.Group className="mb-3 d-flex justify-content-center">
                    <div>
                      <ReCAPTCHA
                        sitekey="6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI"
                        onChange={onCaptchaChange}
                      />
                      {validationErrors.captcha && (
                        <div className="text-danger small mt-1">
                          {validationErrors.captcha}
                        </div>
                      )}
                    </div>
                  </Form.Group>

                  <Button
                    variant="primary"
                    type="submit"
                    className="w-100"
                    disabled={loading}
                  >
                    {loading ? 'Registrando...' : 'Crear Cuenta'}
                  </Button>
                </Form>

                <div className="d-flex justify-content-center align-items-center mt-4">
                  <span className="fw-normal">
                    ¿Ya tienes una cuenta?
                    <Card.Link as={Link} to={Routes.Signin.path} className="fw-bold">
                      {` Iniciar sesión `}
                    </Card.Link>
                  </span>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </main>
  );
};
